import BaseScene from '@Scenes/BaseScene';
import EventEmitter from '@Utils/Events/EventEmitter';

/*--------------- Config ------------------------*/
import * as Scenes from '@Config/scenes';

/*--------------- Core ------------------------*/
import Game from '@Game';
import Network from '@Networks/Localhost';
import AnalyticsEvents from '@Config/analytics_events';

/**
 * Continue Screen
 * Displays score and tricks
 */
class ContinueScreen extends BaseScene
{
    /**
     * Game objects
     */
    private score: Phaser.GameObjects.Text;
    private continueButton: Phaser.GameObjects.Container;
    private endRunButton: Phaser.GameObjects.Text;
    private continueVideoImage: Phaser.GameObjects.Image;
    private loadingCircle: Phaser.GameObjects.Graphics;

    /**
     * Contructor
     */
    constructor()
    {
        super(Scenes.CONTINUE)
    }

    /**
     * Create score scene
     */
    public async create()
    {
        this.enableShutdown();

        this.scene.stop(Scenes.SCORE);

        try
        {
            await this.showContinueScreen();
        }
        catch (error)
        {
            this.showEndScreen();
        }
    }

    /**
     * Shows end screen
     */
    private async showEndScreen()
    {
        this.scene.launch(Scenes.COMPLETE);
        this.scene.stop(Scenes.CONTINUE);
    }

    /**
    * Create loading circle
    */
    private createLoadingCircle()
    {
        const gameSize = this.getGameSize();

        const loadingCircle = this.add.graphics();
        loadingCircle.lineStyle(2, 0x000000);
        loadingCircle.beginPath();
        loadingCircle.arc(0, 0, 10, 0, (Math.PI * 2) * 0.85);
        loadingCircle.strokePath();
        loadingCircle.x = gameSize.width / 2;
        loadingCircle.y = 280;
        loadingCircle.visible = false;

        this.loadingCircle = loadingCircle;
    }

    /**
     * Shows continue screen
     */
    private async showContinueScreen()
    {
        if (Game.adsManager.hasRewardedVideoSupport())
        {
            if (Game.adsManager.isRewardedVideoLoaded())
            {
                this.createLoadingCircle();
                this.createScoreText();
                this.createContinueButton();
                this.createEndRunButton();
            }
            else
            {
                this.showEndScreen();
            }
        }
        else
        {
            this.showEndScreen();
        }
    }

    /**
     * Creates score text
     */
    private createScoreText()
    {
        const gameSize = this.getGameSize();
        const fontStyle = { fontFamily: 'Permanent Marker' };
        const score = Game.runScore.getTotalScore().toString();
        const scoreText = this.add.text(gameSize.width / 2, 170, score, fontStyle);
        scoreText.setOrigin(0.5);
        scoreText.setColor('#000000');
        scoreText.setFontSize(55);
        scoreText.alpha = 0;

        this.add.tween({
            targets: [scoreText],
            alpha: 1,
            duration: 1000,
        });

        this.score = scoreText;
    }

    /**
    * Creates start over button
    */
    private createEndRunButton()
    {
        const gameSize = this.getGameSize();
        const fontStyle = { fontFamily: 'Permanent Marker' };
        const endRunText = this.add.text(gameSize.width / 2, (gameSize.height / 2) + 100, 'End Run', fontStyle);
        endRunText.setOrigin(0.5);
        endRunText.setColor('#777777');
        endRunText.setFontSize(45);

        const textBounds = endRunText.getBounds();
        const hitArea = new Phaser.Geom.Rectangle(0, 0, textBounds.width, textBounds.height);
        endRunText.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
        endRunText.on('pointerdown', this.onEndRunDown);
        endRunText.on('pointerup', this.onEndRunUp);
        endRunText.alpha = 0;

        this.add.tween({
            targets: [endRunText],
            alpha: 1,
            duration: 1000,
        });

        this.endRunButton = endRunText;
    }

    /**
     * Fires when end run text is pressed
     */
    private onEndRunDown = () =>
    {
        this.endRunButton.setColor('#000000');
    }

    /**
     * Fires when end run text is released
     */
    private onEndRunUp = () =>
    {
        Network.logEvent(AnalyticsEvents.PLAYER_END_RUN);
        this.endRunButton.setColor('#777777');
        this.showEndScreen();
    }


    /**
     * Creates a continue button
     */
    private createContinueButton()
    {
        const gameSize = this.getGameSize();
        const buttonContainer = this.add.container(gameSize.width / 2, gameSize.height / 2);
        const fontStyle = { fontFamily: 'Permanent Marker' };

        const continueText = this.add.text(40, 0, 'Continue', fontStyle);
        continueText.setColor('#000000');
        continueText.setOrigin(0, 0.5);
        continueText.setFontSize(35);

        /**------------- Video Image ---------------*/
        const continueImage = this.add.image(0, 0, 'watch_video_icon');
        continueImage.setDisplaySize(40, 40);
        this.continueVideoImage = continueImage;

        /*--------- Set Container Position --------*/
        buttonContainer.add([continueImage, continueText]);
        const containerBounds = buttonContainer.getBounds();
        buttonContainer.setX((gameSize.width / 2) - (containerBounds.width / 2));

        /*---------- Enable Container Input ---------*/
        var hitArea = new Phaser.Geom.Rectangle(-20, -20, containerBounds.width, containerBounds.height);
        buttonContainer.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
        buttonContainer.on('pointerdown', this.onContinueDown);
        buttonContainer.on('pointerup', this.onContinueUp);
        buttonContainer.alpha = 0;

        this.continueButton = buttonContainer;

        this.add.tween({
            targets: [buttonContainer],
            alpha: 1,
            duration: 1000,
        })
    }

    /**
     * Fires when continue is pressed
     */
    private onContinueDown = () =>
    {
        this.continueVideoImage.setTexture('watch_video_icon_hover');
    }

    /**
     * Fires when continue is released
     */
    private onContinueUp = async () =>
    {
        Network.logEvent(AnalyticsEvents.PLAYER_CONTINUE);

        this.continueVideoImage.setTexture('watch_video_icon');

        this.loadingCircle.visible = true;
        this.continueButton.visible = false;
        this.score.visible = false;
        this.endRunButton.visible = false;

        try
        {
            await this.showRewardedVideo();

            EventEmitter.emit(Scenes.CONTINUE, 'videoWatched');

            Network.logEvent(AnalyticsEvents.VIDEO_WATCHED);
        }
        catch (error)
        {
            EventEmitter.emit('debug', 'log', error);

            Network.logEvent(AnalyticsEvents.FAILED_TO_SHOW_VIDEO, null, { error_code: error.code });

            this.showEndScreen();
        }
    }

    public update()
    {
        if (this.loadingCircle)
        {
            this.loadingCircle.angle += 10;
        }
    }

    /**
     * Shows rewarded video
     */
    private async showRewardedVideo()
    {
        await Game.adsManager.showRewardedVideo();
    }


}

export default ContinueScreen;